'use client'

import { batch } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import Icon from '@susu/headless-commerce/components/Icon/Icon'
import Loader from '@susu/headless-commerce/components/Loader/Loader'
import { useCountries } from '@susu/headless-commerce/contexts/countries/client'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import styles from '@susu/headless-commerce/features/navigation/components/NavCountrySelector/NavCountrySelector.module.scss'
import { getCountryInitial } from '@susu/headless-commerce/features/navigation/components/NavCountrySelector/utils/getCountryInitial'
import { searchFilter } from '@susu/headless-commerce/features/navigation/components/NavCountrySelector/utils/searchFilter'
import { useGetCountriesOrder } from '@susu/headless-commerce/hooks/useGetCountriesOrder'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import type { CountryConfiguration } from '@susu/headless-commerce/types/CountryConfiguration'
import { highlightText } from '@susu/headless-commerce/utils/highlightText'
import { pathnameWithoutLocalePrefix } from '@susu/headless-commerce/utils/pathname'
import { assertNewCountryValue } from '@susu/headless-commerce/utils/tracking/assertNewCountryValue'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackEvent } from '@susu/headless-commerce/utils/tracking/tracking'
import { useI18N } from '@susu/i18n/client'
import classNames from 'classnames'
import { usePathname } from 'next/navigation'
import type { CSSProperties, ChangeEventHandler } from 'react'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import { useClientNav } from '../ClientNavProvider'

const countryListClass = classNames(styles['country-selector'])
const searchClass = classNames(styles['country-selector__search'])
const searchInputClass = classNames(styles['country-selector__search-input'])
const clearButtonClass = classNames(styles['country-selector__clear-btn'])
const countryLetterClass = classNames(styles['country-selector__letter'])

const loaderStyle = {
  position: 'absolute',
  top: '0',
  bottom: '0',
  left: '0',
  right: '0',
  display: 'grid',
  placeItems: 'center',
} as CSSProperties

export const ClientNavCountrySelector = () => {
  useSignals()

  const pageType = usePageType()
  const country = useCountry()
  const locale = useLocale()
  const countries = useCountries()
  const { data: order } = useGetCountriesOrder()
  const { criteria, hasCriteria, setCriteria, clearCriteria } = useClientNav()
  const { isCountrySelectorOpenedFromFooter } = useClientNav()
  const pathname = usePathname()
  const initials = new Set<string>()
  const selectorRef = useRef<HTMLDivElement | null>(null)
  const countryInputRef = useRef<HTMLInputElement | null>(null)
  const { t } = useI18N()

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.target.value) {
        setCriteria(event.target.value)
      } else {
        clearCriteria()
      }
      setCriteria(event.target.value ?? '')
    },
    [clearCriteria, setCriteria],
  )

  const handleClearInputClick = useCallback(() => {
    batch(() => {
      clearCriteria()
      countryInputRef.current?.focus()
    })
  }, [clearCriteria])

  const createHandleClick = useCallback(
    (countryCode: string) => {
      return async () => {
        const { url, locationId } = URLData()

        try {
          await trackEvent({
            segment: {
              event: 'countryChanged',
              enrichProps: {
                locale,
                country,
              },
              properties: {
                pageType: pageType,
                eventCategory: isCountrySelectorOpenedFromFooter.value
                  ? 'footer_interactions'
                  : 'global_interactions',
                eventLabel: '',
                eventLocation: isCountrySelectorOpenedFromFooter.value
                  ? 'footer'
                  : 'menu',
                url,
                locationId,
                newCountry: assertNewCountryValue(countryCode.toLowerCase()),
              },
            },
          })
        } catch (error) {
          console.error('Failed to track country change:', error)
        }
      }
    },
    [country, locale, pageType, isCountrySelectorOpenedFromFooter],
  )

  useEffect(() => {
    initials.clear()
  }, [initials.clear])

  const filtered = useMemo(
    () =>
      order
        ? searchFilter(Object.values(countries), criteria.value, order)
        : [],
    [criteria.value, order, countries],
  )

  if (!order) {
    return (
      <div style={loaderStyle} data-testid="nav-country-selector">
        <Loader />
      </div>
    )
  }

  const countriesClass = classNames(styles['country-selector__countries'])
  const countryLinkClass = classNames(styles['country-selector__link'], {
    [styles['country-selector__link--highlighted']]: hasCriteria(),
  })
  return (
    <div
      ref={selectorRef}
      className={countryListClass}
      data-testid="nav-country-selector"
    >
      <div className={searchClass}>
        <label className="sr-only" htmlFor="country-search-input">
          {t('countrySelector.search_label')}
        </label>
        <input
          className={searchInputClass}
          data-testid="country-selector_search"
          id="country-search-input"
          onChange={handleSearchChange}
          ref={countryInputRef}
          placeholder={t('countrySelector.search_label')}
          type="text"
          value={criteria.value}
        />
        {hasCriteria() && (
          <button
            type="button"
            className={clearButtonClass}
            onClick={handleClearInputClick}
            data-testid="country-selector_clear-search"
          >
            <Icon icon="close" aria-hidden="true" />
            <span className="sr-only">Clear</span>
          </button>
        )}
      </div>
      <div
        className={countriesClass}
        data-testid="country-selector_search-results"
      >
        {filtered.length === 0 ? (
          <span
            className={styles['country-selector__noresults-text']}
            data-testid="label-no-results-country-selector"
          >
            {t('navigation.no_results')}{' '}
            <span className={'body-medium'}>{`'${criteria.value}'`}</span>
          </span>
        ) : (
          <>
            {filtered.map(
              ({ countryCode, name, locale }: CountryConfiguration) => {
                const initial = getCountryInitial(name)
                const isFirst = !initials.has(initial)

                initials.add(initial)

                return (
                  <Fragment key={countryCode}>
                    {isFirst && !hasCriteria() && (
                      <span className={countryLetterClass}>{initial}</span>
                    )}
                    <a
                      className={countryLinkClass}
                      onClick={createHandleClick(countryCode)}
                      data-name={name}
                      data-locale={locale}
                      href={`/${locale}${pathnameWithoutLocalePrefix(pathname)}`}
                      data-testid={`country-selector_link_${countryCode}`}
                    >
                      {hasCriteria()
                        ? highlightText(name, criteria.value)
                        : name}
                    </a>
                  </Fragment>
                )
              },
            )}
          </>
        )}
      </div>
    </div>
  )
}
